<template>
  <div class="project-slider">
    <swiper
      :options="parentSwiperData"
      class="parent-project-container swiper-container swiper-height-full"
      ref="parentSwiper"
    >
      <swiper-slide
        v-for="slide in data"
        :key="slide.sys.id"
        :ref="slide.sys.id"
        class="swiper-slide"
      >
        <div
          class="project-description is-collapsed"
          uk-grid
          @click="ukToggle(slide.sys.id)"
        >
          <div class="uk-width-expand">
            <p class="project-title" v-text="slide.fields.title"></p>
            <div class="project-extra is-hidden">
              <vue-markdown
                :html="true"
                v-fancy-letters.multiple="'a'"
                :anchor-attributes="{
                  target: '_blank',
                  rel: 'nofollow',
                }"
                >{{ slide.fields.description }}</vue-markdown
              >

              <div
                class="uk-badge"
                v-text="slide.fields.category.fields.categoryName"
              ></div>
            </div>
          </div>
          <div class="uk-width-auto">
            <button class="show-more uk-button uk-button-link uk-float-right">
              <span>Show More</span>
              <span
                class="uk-margin-small-right"
                uk-icon="icon: triangle-down"
              ></span>
            </button>
          </div>
        </div>

        <div class="media-container">
          <template v-for="(images, k) in slide.fields.portfolioImages">
            <template v-if="k === 0">
              <AutoVideoPlayer
                class="video"
                :threshold="0.1"
                v-if="images.fields.file.contentType.indexOf('video') === 0"
                :key="`video-${images.sys.id}`"
                :src="images.fields.file.url"
                @loadedmetadata="onMediaLoaded"
              />
              <img
                v-else-if="
                  images.fields.file.contentType.indexOf('image') === 0
                "
                :key="`img-${images.sys.id}`"
                :src="`${
                  images.fields.file.url +
                  imageQueryString(images.fields.file.contentType)
                }`"
                :alt="
                  images.fields.file.description
                    ? images.fields.file.description
                    : 'Fallback alt'
                "
                @load="onMediaLoaded"
              />
            </template>
          </template>
        </div>
      </swiper-slide>
      <!-- Add Arrows -->
      <div :class="['swiper-button-prev', 'uk-visible@s']" slot="button-prev">
        <anim-tongue class="tongue" :width="200" :height="200" />
      </div>
      <div :class="['swiper-button-next', 'uk-visible@s']" slot="button-next">
        <anim-tongue class="tongue" :width="200" :height="200" />
      </div>

      <div class="swiper-scrollbar" slot="scrollbar" />
    </swiper>
    <img v-if="hintKeys" class="hint-keys" src="/static/img/keys.png" />
  </div>
</template>

<script>
import AnimTongue from '@/components/AnimTongue'
import VueMarkdown from 'vue-markdown'
import AutoVideoPlayer from './AutoVideoPlayer.vue'

export default {
  components: {
    AnimTongue,
    VueMarkdown,
    AutoVideoPlayer,
  },
  props: ['data'],
  data() {
    return {
      hintKeys: false,
      parentSwiperData: {
        preloadImages: false,
        direction: 'horizontal',
        loop: false,
        speed: 1500,
        slidesPerView: 'auto',
        observer: true,
        observeParents: true,
        centeredSlides: false,
        spaceBetween: 0,
        freeMode: true,
        watchSlidesProgress: true,
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
          snapOnRelease: false,
          dragSize: 32,
        },
        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
        grabCursor: true,
        simulateTouch: true,
        // Navigation arrows
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        on: {
          imagesReady: () => {
            this.$nextTick(() => {
              this.getSlideWidth(this.swiper)
            })
          },
          resize: () => {
            this.$nextTick(() => {
              this.getSlideWidth(this.swiper)
            })
          },
        },
      },
      childSwiperData: {
        direction: 'vertical',
        slidesPerView: 1,
        paginationClickable: true,
        pagination: '.child-project-container-pagination',
        // Disable preloading of all images
        preloadImages: false,
        simulateTouch: true,
      },
    }
  },
  computed: {
    swiper() {
      return this.$refs.parentSwiper.swiper
    },
  },
  mounted() {
    const { swiper } = this

    swiper.scrollbar.dragEl.innerHTML =
      '<img src="/static/img/nipple.png" class="custom-scrollbar-icon"> '

    setTimeout(() => {
      this.hintKeys = false
    }, 5000)

    // Add resize listener to recalculate slide widths
    this.resizeHandler = () => {
      this.$nextTick(() => {
        this.getSlideWidth(this.swiper)
      })
    }
    window.addEventListener('resize', this.resizeHandler)
  },
  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  },
  methods: {
    onMediaLoaded() {
      // Media loaded - let CSS handle sizing
      this.$nextTick(() => {
        this.swiper.update()
      })
    },
    imageQueryString(contentType) {
      let qs = '?q=85&fl=progressive&h=1080'
      if (contentType !== 'image/gif') qs += '&fm=jpg'
      return qs
    },
    isMobile() {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        return true
      } else {
        return false
      }
    },
    ukToggle(hash) {
      const projectContainer = this.$refs[hash][0].$el.firstElementChild
      if (projectContainer.classList.contains('is-collapsed')) {
        projectContainer.classList.remove('is-collapsed')
      } else {
        projectContainer.classList.add('is-collapsed')
      }
    },
    getSlideWidth({ slides }) {
      // Let CSS handle the sizing completely
      this.swiper.update()
    },
  },
}
</script>

<style lang="scss">
$tongue-size: 200px;
$scrollbar-offset: 13px;
$scrollbar-position: 2px;

.hint-keys {
  position: absolute !important;
  top: 45%;
  left: 50%;
  z-index: 1;
  width: 12%;
  translate: -50% -50%;
  user-select: none;
  pointer-events: none;
}

.project-slider {
  position: relative;
  height: 65vh;
  @include mq($from: tablet) {
    height: 83vh;
    min-height: 480px;
  }
  @include mq($until: desktop, $and: '(orientation: landscape)') {
    height: calc(100vh - #{$nav-min-height});
    min-height: calc(100vh - #{$nav-min-height});
  }
}

.parent-project-container,
.child-project-container {
  height: 100%;
}

// Parent Slide
.parent-project-container {
  overflow-x: hidden;
  overflow-y: unset !important;
  @include cursor();

  .swiper-slide {
    position: relative;
    overflow: hidden;
    height: calc(100% - #{$scrollbar-offset});
    width: auto !important; // Let content determine width
    flex-shrink: 0; // Prevent shrinking

    // Responsive width based on viewport
    @include mq($until: tablet) {
      min-width: 280px;
      max-width: 90vw;
    }

    @include mq($from: tablet) {
      min-width: 400px;
      max-width: 85vw;
    }

    .media-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .media-container img,
    .media-container .video {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
      display: block;
    }

    // For portrait images/videos, limit width
    .media-container img[style*='height'],
    .media-container .video[style*='height'] {
      height: 100%;
      width: auto;
    }

    .uk-button {
      margin-top: 3px;
    }
  }

  .swiper-button-next {
    width: $tongue-size;
    height: $tongue-size;
    top: calc(50% - 8px);
    right: -65px;
    background-image: none !important;
    transform: translateY(-50%);
    .tongue {
      width: $tongue-size;
      height: $tongue-size;
    }
  }
  .swiper-button-prev {
    width: $tongue-size;
    height: $tongue-size;
    left: -65px;
    background-image: none !important;
    transform: translateY(-50%);
    .tongue {
      width: $tongue-size;
      height: $tongue-size;
      transform: rotate(180deg); // translateX(-150%) translateY(30%);
    }
  }

  .disabled {
    // opacity: 0.35;
    cursor: auto;
    pointer-events: none;
  }
}

.project-description {
  display: flex;
  width: 100%;
  position: absolute;
  bottom: 0px;
  left: 0;
  padding: 0 5px $scrollbar-offset * 2 !important; // override uk-grid padding: 0;
  margin-left: 0 !important;
  background-color: rgba($grey, 0.9);
  color: $slider-text-color;
  transition: $transition;
  z-index: 99;

  @include mq($from: tablet) {
    display: flex;
  }

  &.is-collapsed {
    // Adjust negative element for height of top bar
    transform: translateY(calc(100% - 39px - #{$scrollbar-position}));
    transition: $transition;

    @include mq($from: xlarge) {
      transform: translateY(
        calc(100% - #{get-vw(39px)} - #{get-vw($scrollbar-position)})
      );
    }
  }

  .project-title {
    margin-top: 3px;
    margin-bottom: 20px;
    font-size: 16px;
    color: $primary;

    @include mq($from: xlarge) {
      font-size: get-vw(16px);
      margin-bottom: get-vw(20px);
    }
    @include truncate();
  }

  .swiper-pagination-bullet {
    margin: 0 6px;
  }
}

.show-more {
  font-size: 14px;
  line-height: 24px;
  @include mq($from: xlarge) {
    font-size: get-vw(16px);
    line-height: 1.5;
  }

  svg {
    width: 20px;
    height: 20px;
    @include mq($from: xlarge) {
      width: get-vw(20px);
      height: get-vw(20px);
    }
  }
}

.project-container,
.project-image {
  background-image: none !important;
  background-size: contain;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.swiper-scrollbar {
  bottom: 15px !important;
}
// Swiper scrollbar drag
.swiper-container-horizontal > .swiper-scrollbar {
  bottom: 13px;
}
.swiper-scrollbar-drag {
  background: none;

  .custom-scrollbar-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PortfolioHorizontal.vue